import React, { useEffect } from "react";
import { useState } from "react";
import SuccessIcon from "../assets/img/notifications/success-icon.svg";
import WarningIcon from "../assets/img/notifications/warning-icon.svg";
import InfoIcon from "../assets/img/notifications/Info-icon.svg";
import ReactTimeAgo from "react-time-ago";
import {
  useGetNotificationsQuery,
  useSeenNotificationsMutation,
} from "../redux/slices/api/userApi";
import { Bars, Oval } from "react-loader-spinner";
import { useNavigate } from "react-router-dom";
import { handleError } from "../hooks/useApi";
import { selectGeneral } from "../redux/slices/generalSlice";
import { useSelector } from "react-redux";
import MetaTags from "../components/common/MetaTags";
const TabButton = ({ text, count, activeTab, setActiveTab }) => {
  let isActive = text === activeTab;
  return (
    <button
      onClick={() => setActiveTab(text)}
      type="button"
      className={`${isActive ? "" : ""} flex flex-col pb-1 `}
    >
      <span className="flex items-center">
        <span
          className={`${
            isActive ? "text-primary" : "text-custom-gray-500"
          } font-bold`}
        >
          {text}
        </span>
        <span className="h-6 w-6 flex justify-center items-center rounded-md text-sm text-custom-gray-600 bg-custom-gray-300 ml-2">
          {count}
        </span>
      </span>
      {isActive && (
        <span className="border-2 border-primary w-full rounded-2xl mt-1 mr-1"></span>
      )}
    </button>
  );
};

// Functional component representing a single notification item
const NotifItem = ({ data, MarkAsRead }) => {
  // Define notification types and corresponding icons
  const notifTypes = {
    success: {
      icon: SuccessIcon,
    },
    warning: {
      icon: WarningIcon,
    },
    alert: {
      icon: WarningIcon,
    },
    info: {
      icon: InfoIcon,
    },
  };

  // Destructure notification data
  let { type, title, description, createdAt, seen, action, _id } = data ?? {};

  // Custom hook for navigation
  const navigate = useNavigate();

  // Function to handle redirection to the specified action and mark notification as read
  const handleRedirectToAction = () => {
    if (action) {
      let url = action.split("/").slice(3).join("/");
      navigate("/" + url);
      MarkAsRead(_id);
    }
  };

  // Render the notification item
  return (
    <button
      onClick={handleRedirectToAction}
      className={`w-full min-h-[5rem] py-3 flex flex-col px-5 hover:bg-slate-200 ${
        seen ? "bg-whitesmoke " : " bg-custom-gray-300"
      }`}
    >
      <div className="w-full flex items-start justify-between">
        <div className="flex items-center">
          {/* Display notification type icon */}
          <span className="h-8 w-8 pl-0.5 pb-0.5 flex justify-center items-center bg-[#fefefe] rounded-full">
            <img src={notifTypes[type]?.icon} alt={type} className="w-4 h-4" />
          </span>
          {/* Display notification title */}
          <span className="text-secondary font-semibold hover:text-primary ml-2">
            {title}
          </span>
        </div>
        {/* Display notification timestamp */}
        <span className="flex flex-col text-center pt-1">
          <ReactTimeAgo
            date={createdAt}
            locale="en-US"
            className="text-xs text-custom-gray-600"
            timeStyle="twitter"
          />
        </span>
      </div>
      {/* Display notification description */}
      <span className="max-w-[90%] text-custom-gray-800 text-sm text-left mt-1.5 pl-3">
        {description}
      </span>
    </button>
  );
};

// Functional component representing a tab with a list of notifications
const Tab = ({ data = [], name, activeTab, MarkAsRead }) => {
  // Check if the tab is currently active
  let isActive = name === activeTab;

  // Render notifications if the tab is active and contains data
  if (isActive) {
    if (data?.length) {
      return (
        <div className="w-full flex flex-col mt-7">
          {/* Sort and map through each notification item */}
          {[...data]
            ?.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
            .map((notif, index) => (
              <NotifItem data={notif} MarkAsRead={MarkAsRead} key={index} />
            ))}
        </div>
      );
    } else {
      // Display a message if no notifications are found
      return (
        <div className="w-full h-full flex flex-col justify-center items-center mt-7 bg-custom-gray-100">
          No notification found!
        </div>
      );
    }
  }
  return null;
};

// Functional component representing the Notifications section
const Notifications = () => {
  // State to track the active tab
  const [activeTab, setActiveTab] = useState("All");

  // Redux selector to check if the user is logged in
  const { isLoggedIn } = useSelector(selectGeneral);

  // Query to fetch notification data
  const {
    data: notifData,
    isLoading: notifLoading,
    refetch,
  } = useGetNotificationsQuery(undefined, { skip: !isLoggedIn });

  // Mutation to mark notifications as seen
  const [seenNotifications, { isLoading: seenNotifLoading }] =
    useSeenNotificationsMutation();

  // Function to mark a notification as read
  const MarkAsRead = async (id) => {
    try {
      // Determine the notification ids to mark as read
      let ids = id
        ? [id]
        : notifData?.filter((item) => !item?.seen).map((item) => item?._id);

      // Call the mutation to mark notifications as seen
      await seenNotifications({
        body: {
          ids,
        },
      }).unwrap();

      // Refetch the notification data
      refetch();
    } catch (error) {
      // Handle errors during the process
      handleError(error, null, true);
    }
  };

  // Function to check if unread notifications exist
  const unReadExists = () => notifData?.filter((item) => !item?.seen)?.length;

  // Effect to handle scrolling and auto-marking notifications as read
  useEffect(() => {
    if (unReadExists()) refetch();
    const onScroll = (e) => {
      if (window.scrollY > 50 && unReadExists() && !seenNotifLoading) {
        MarkAsRead();
      }
    };
    window.addEventListener("scroll", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
    };
    //eslint-disable-next-line
  }, [notifData, seenNotifLoading]);

  // Render loading spinner while fetching data
  if (notifLoading) {
    return (
      <div className="w-full h-screen flex justify-center items-center">
        <Bars
          height="50"
          width="50"
          color="#8538ff"
          ariaLabel="bars-loading"
          wrapperStyle={{}}
          wrapperClass=""
          visible={true}
        />
      </div>
    );
  } else
    return (
      <div className="w-full flex justify-center min-h-screen pt-40 pb-10">
        <MetaTags
          title="Notifications"
          description={`NFT- Fundraising By FunDeSci An innovative approach for decentralized crowdfunding of scientific projects`}
          link="/notifications"
        />
        <div className="w-full md:max-w-xl flex flex-col">
          <div className="w-full flex justify-between items-center max-sm:px-5">
            {/* Tab buttons for different notification categories */}
            <TabButton
              activeTab={activeTab}
              count={notifData?.length || 0}
              text={"All"}
              setActiveTab={setActiveTab}
            />
            <TabButton
              activeTab={activeTab}
              count={0}
              text={"Requests"}
              setActiveTab={setActiveTab}
            />
            <TabButton
              activeTab={activeTab}
              count={0}
              text={"Messages"}
              setActiveTab={setActiveTab}
            />
          </div>
          {unReadExists() ? (
            <div className="w-full mt-3">
              {/* Button to mark all notifications as read */}
              {seenNotifLoading ? (
                <Oval
                  height={22}
                  width={22}
                  color="#8538FF"
                  wrapperStyle={{}}
                  wrapperClass=""
                  visible={true}
                  ariaLabel="oval-loading"
                  secondaryColor="#8538FF"
                  strokeWidth={2}
                  strokeWidthSecondary={2}
                />
              ) : (
                <button
                  className="text-primary max-sm:px-5"
                  onClick={() => MarkAsRead()}
                >
                  Mark all as read
                </button>
              )}
            </div>
          ) : null}
          {/* Tab components displaying notifications based on the active tab */}
          <Tab
            name={"All"}
            data={notifData}
            activeTab={activeTab}
            MarkAsRead={MarkAsRead}
          />
          <Tab name={"Requests"} data={[]} activeTab={activeTab} />
          <Tab name={"Messages"} data={[]} activeTab={activeTab} />
        </div>
      </div>
    );
};

export default Notifications;
